<template>
  <div class="custom-select" :tabindex="tabindex" @blur="handleBlur">
    <div class="selected cursor-pointer block p-3 text-neutral-700" :class="{ open: open }" @click="open = !open">
      <span v-if="placeholderText" class="placeholder">
        {{ placeholderText }}
      </span>

      <template v-else>
        <span v-if="optionLabel && selected">
          {{ selected[optionLabel] }}
        </span>
        <span v-else>
          {{ selected }}
        </span>
      </template>

      <span class="drop-arrow absolute right-3 top-1/2 transform -translate-y-1/2" :class="{ expanded: open }">
        <Chevron direction="down" width="14" height="14" />
      </span>
    </div>

    <div class="content" :class="{ selectHide: !open }">
      <div v-if="enableSearch" class="p-4 bg-neutral-200 shadow-border-b flex gap-2 items-center">
        <Search width="16" height="16" />
        <input type="text" class="w-full bg-transparent outline-none border-none text-sm" placeholder="Cari" v-model="searchKeyword" @keyup.enter="search" />
      </div>

      <template v-if="options && options.length">
        <div v-for="(option, i) in options" :key="i" class="p-3 text-neutral-700 hover:bg-neutral-200 cursor-pointer" :class="{ 'bg-neutral-200': isSelected(option) }" @click="selectItem(option)">
          <span v-if="optionLabel">
            {{ option[optionLabel] }}
          </span>
          <span v-else>
            {{ option }}
          </span>
        </div>
      </template>

      <template v-else>
        <span class="flex justify-center items-center py-2 text-neutral-500 text-sm">
          No data is available
        </span>
      </template>
    </div>
  </div>
</template>

<script>
export default {
  name: 'CustomSelect',

  components: {
    Chevron: () => import('@/components/Icons/Chevron'),
    Search: () => import('@/components/Icons/Search')
  },

  props: {
    value: {
      required: false
    },

    options: {
      type: Array,
      default: () => []
    },

    optionLabel: {
      type: String,
      default: null
    },

    optionValue: {
      type: String,
      default: null
    },

    placeholder: {
      type: String,
      default: 'Pilih'
    },

    enableSearch: {
      type: Boolean,
      default: false
    },

    tabindex: {
      type: Number,
      default: 0
    }
  },

  data() {
    return {
      selected: null,
      open: false,
      placeholderText: this.placeholder,
      searchKeyword: ''
    }
  },

  watch: {
    value: {
      immediate: true,
      handler(val) {
        if (!val) {
          this.selected = null
          this.placeholderText = this.placeholder
          return
        }

        if (this.optionValue) {
          this.selected = this.options.find(opt => opt[this.optionValue] === val) || null
        } else {
          this.selected = val
        }

        this.placeholderText = null
      }
    }
  },

  methods: {
    handleBlur(e) {
      if (!e.currentTarget.contains(e.relatedTarget)) {
        this.open = false
      }
    },

    isSelected(option) {
      if (!this.selected) return false

      if (this.optionValue) {
        return option[this.optionValue] === this.selected[this.optionValue]
      }

      return option === this.selected
    },

    selectItem(option) {
      this.selected = option
      this.open = false
      this.placeholderText = null

      const emittedValue = this.optionValue ? option[this.optionValue] : option

      this.$emit('input', emittedValue)
      this.$emit('change', emittedValue)
    },

    search() {
      this.$emit('search', this.searchKeyword)
    }
  }
}
</script>

<style lang="scss" scoped>
.custom-select {
  position: relative;
  width: 100%;
  text-align: left;
  outline: none;
}

.selected {
  background-color: #fff;
  border-radius: 8px;
  border: 1px solid #ced5de;
  position: relative;
  cursor: pointer;
  user-select: none;
  z-index: 1;

  &.open {
    border-bottom-left-radius: 0;
    border-bottom-right-radius: 0;
  }
}

.drop-arrow {
  transition: transform 0.3s cubic-bezier(0.59, 1.39, 0.37, 1.01);

  &.expanded {
    transform: rotateZ(180deg) translateY(7px);
  }
}

.placeholder {
  color: #9ca3af;
}

.content {
  border: 1px solid #ced5de;
  max-height: 250px;
  overflow-y: auto;
  width: 100%;
  position: absolute;
  z-index: 10;
  background: #fff;
  margin-top: -1px;
  border-bottom-left-radius: 8px;
  border-bottom-right-radius: 8px;
}

.selectHide {
  display: none;
}
</style>
